import {
  makeStyles,
  IconButton,
  Hidden,
  Typography,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import {
  getChatControlData,
  setChatDialogOpen,
} from 'components/ChatDialog/store/slices/chatSlice';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Icon from '@mdi/react';
import { mdiClose, mdiMagnify } from '@mdi/js';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ChatListItem from './ChatListItem';
import { selectProjects } from 'store/projects/selectors/projectsSelectors';
import { selectUserActiveEmployerId } from 'store/user/selectors.js/user.selectors';
import { loadChatUsers } from 'components/ChatDialog/store/thunks/loadChatUsers';
import { setThreadWithUsers } from 'components/ChatDialog/store/thunks/loadThreadUsers';
import { getProjectEmailData } from 'store/projectEmail/projectEmailSlice';
import { selectIsRequiredPermissionForProject } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { PROJECT_EMAIL_READ } from 'scenes/Permissions/constants/permissions';
import isFeatureEnabled, { Feature } from 'utils/isFeatureEnabled';

const useStyles = makeStyles((theme) => ({
  root: {
    ...theme.component.dialogPaperFullscreen,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: 320,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    overflowY: 'auto',
  },
  paper: {
    backgroundColor: 'initial',
    border: `1px solid ${theme.palette.borderColor}`,
    boxSizing: 'border-box',
    margin: theme.spacing(1),

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  threadContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    overflow: 'hidden',
    padding: theme.spacing(1.5, 2),
  },
  avatar: {
    width: 30,
    height: 30,
    backgroundColor: 'white',
    fontSize: '0.875rem',
    flexShrink: 0,
  },
  threadTitle: {
    flex: 1,
    fontSize: '0.95rem',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chatButton: {
    ...theme.component.navIcon,
    padding: theme.spacing(0.5),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 46,
    height: '100%',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  closeGrid: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
  },
  closeButton: {
    padding: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  noMessages: {
    textAlign: 'center',
    padding: theme.spacing(4, 2),
    color: theme.palette.text.secondary,
    fontSize: '2rem',
  },
  projectHeader: {
    padding: theme.spacing(2, 2, 1, 2),
    fontWeight: 600,
    fontSize: '1.05rem',
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    '&:not(:first-of-type)': {
      marginTop: theme.spacing(1),
    },
  },
  searchContainer: {
    padding: theme.spacing(2),
    backgroundColor: 'transparent',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper,
    },
  },
}));

const ChatMenuContent = ({ onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { threads, allUsers } = useSelector(getChatControlData);
  const allProjects = useSelector(selectProjects);
  const activeEmployerId = useSelector(selectUserActiveEmployerId);

  const projects = React.useMemo(() => {
    if (!activeEmployerId) return allProjects;
    return Object.fromEntries(
      Object.entries(allProjects).filter(
        ([, project]) =>
          project.masterParent === activeEmployerId ||
          project.id === activeEmployerId
      )
    );
  }, [allProjects, activeEmployerId]);

  const {
    unreadMessages,
    emailMessagesCounts,
    emailLastMessageAt = {},
  } = useSelector(getProjectEmailData);

  const [searchQuery, setSearchQuery] = useState('');

  const { t, i18n } = useTranslation('common');

  const handleChatButtonClick = React.useCallback(
    (thread) => {
      // Set the thread and load its users
      dispatch(setThreadWithUsers({ thread }));

      // Open the dialog
      dispatch(setChatDialogOpen(true));

      // Close the chat menu so it isn't left open in front of the dialog
      if (onClose) {
        onClose();
      }
    },
    [dispatch, onClose]
  );

  const collator = React.useMemo(() => {
    try {
      return new Intl.Collator(i18n.language);
    } catch (e) {
      return new Intl.Collator('en');
    }
  }, [i18n.language]);

  const sortAlphabetically = React.useCallback(
    (a, b) => {
      const lowerA = (a || '').toLowerCase();
      const lowerB = (b || '').toLowerCase();
      try {
        return collator.compare(lowerA, lowerB);
      } catch (e) {
        return lowerA.localeCompare(lowerB);
      }
    },
    [collator]
  );

  // Memoize filtering of threads by search query
  const filteredThreadsByProject = React.useMemo(() => {
    return Object.values(threads).reduce((acc, thread) => {
      const projectId = thread.projectId;
      if (!projectId || !projects[projectId]) {
        return acc;
      }
      const projectName = (projects[projectId]?.name || '').toLowerCase();
      const threadTitle = (thread.title || '').toLowerCase();
      const query = searchQuery.toLowerCase();
      if (!query) {
        if (!thread.hasMessages) return acc;
        if (!acc[projectId]) acc[projectId] = [];
        acc[projectId].push(thread);
        return acc;
      }
      const projectMatches = projectName.includes(query);
      const threadMatches = threadTitle.includes(query);
      if (projectMatches) {
        if (!acc[projectId]) acc[projectId] = [];
        acc[projectId].push(thread);
        return acc;
      }
      if (threadMatches) {
        if (!acc[projectId]) acc[projectId] = [];
        acc[projectId].push(thread);
        return acc;
      }
      return acc;
    }, {});
  }, [threads, projects, searchQuery]);

  // Memoize sorting of threads within each project
  const threadsByProject = React.useMemo(() => {
    const grouped = { ...filteredThreadsByProject };
    // Sort threads within each project
    Object.keys(grouped).forEach((projectId) => {
      grouped[projectId].sort((a, b) => {
        // Thread type priority: 0 = main project thread, 1 = private/direct, 2 = subthread
        const typeOf = (t) => {
          if (t.isSubthread) return 2;
          if (t.directRecipient) return 1;
          return 0;
        };
        const aType = typeOf(a);
        const bType = typeOf(b);
        if (aType !== bType) return aType - bType;
        // Threads with messages before threads without messages
        if (a.hasMessages && !b.hasMessages) return -1;
        if (!a.hasMessages && b.hasMessages) return 1;
        // Both have no messages: alphabetical
        if (!a.hasMessages && !b.hasMessages)
          return sortAlphabetically(a.title, b.title);
        // Both have messages: unread tier first
        const aUnread = a.unreadMessagesCount > 0;
        const bUnread = b.unreadMessagesCount > 0;
        if (aUnread && !bUnread) return -1;
        if (!aUnread && bUnread) return 1;
        // Same tier: sort by lastMessageAt DESC
        const aTs = a.lastMessageAt || '';
        const bTs = b.lastMessageAt || '';
        if (aTs > bTs) return -1;
        if (aTs < bTs) return 1;
        return sortAlphabetically(a.title, b.title);
      });
    });
    return grouped;
  }, [filteredThreadsByProject, sortAlphabetically]);

  // Get all employer projects with email enabled and check permissions
  const allEmployerProjectsWithEmail = React.useMemo(
    () =>
      Object.values(projects).filter(
        (project) =>
          project.type === 'EMPLOYER' &&
          isFeatureEnabled(Feature.PROJECT_EMAIL, project)
      ),
    [projects]
  );

  // Get email permissions for employer projects
  const employerProjectEmailPermissions = useSelector((state) => {
    const permissions = {};
    allEmployerProjectsWithEmail.forEach((project) => {
      permissions[project.id] = selectIsRequiredPermissionForProject(state, {
        projectId: project.id,
        permission: PROJECT_EMAIL_READ,
      });
    });
    return permissions;
  });

  // Filter to only include employer projects where user has permission
  const employerProjectsWithEmail = React.useMemo(
    () =>
      allEmployerProjectsWithEmail.filter(
        (project) => employerProjectEmailPermissions[project.id]
      ),
    [allEmployerProjectsWithEmail, employerProjectEmailPermissions]
  );

  // Filter employer projects by search query
  const filteredEmployerProjects = React.useMemo(() => {
    if (!searchQuery) {
      return employerProjectsWithEmail.filter(
        (project) => emailMessagesCounts[project.projectKey] > 0
      );
    }
    const query = searchQuery.toLowerCase();
    return employerProjectsWithEmail.filter((project) => {
      const projectName = (project?.name || '').toLowerCase();
      return projectName.includes(query);
    });
  }, [employerProjectsWithEmail, searchQuery, emailMessagesCounts]);

  const sortedProjectIds = React.useMemo(() => {
    // Get project IDs from threadsByProject
    const projectIdsFromThreads = Object.keys(threadsByProject);

    // Get employer project IDs (these don't have threads)
    const employerProjectIds = filteredEmployerProjects.map(
      (project) => project.id
    );

    // Combine all project IDs
    const allProjectIds = [...projectIdsFromThreads, ...employerProjectIds];

    // Pre-compute latest message timestamp per project for efficient sorting
    const latestTs = {};
    for (const id of allProjectIds) {
      const projectKey = projects[id]?.projectKey;
      const tsList = [
        ...(threadsByProject[id] || [])
          .map((t) => t.lastMessageAt)
          .filter(Boolean),
        emailLastMessageAt[projectKey] || null,
      ].filter(Boolean);
      latestTs[id] =
        tsList.length > 0
          ? tsList.reduce((max, ts) => (ts > max ? ts : max), '')
          : null;
    }

    // Sort all projects together
    return allProjectIds.sort((a, b) => {
      const aIsEmployer = employerProjectIds.includes(a);
      const bIsEmployer = employerProjectIds.includes(b);

      // Check for unread messages
      const aHasUnread = aIsEmployer
        ? unreadMessages[projects[a]?.projectKey] > 0
        : threadsByProject[a]?.some(
            (thread) => thread.unreadMessagesCount > 0
          ) || unreadMessages[projects[a]?.projectKey] > 0;
      const bHasUnread = bIsEmployer
        ? unreadMessages[projects[b]?.projectKey] > 0
        : threadsByProject[b]?.some(
            (thread) => thread.unreadMessagesCount > 0
          ) || unreadMessages[projects[b]?.projectKey] > 0;

      // Check for any messages (read or unread)
      const aHasMessages = aIsEmployer
        ? emailMessagesCounts[projects[a]?.projectKey] > 0
        : threadsByProject[a]?.some((t) => t.hasMessages) ||
          emailMessagesCounts[projects[a]?.projectKey] > 0;
      const bHasMessages = bIsEmployer
        ? emailMessagesCounts[projects[b]?.projectKey] > 0
        : threadsByProject[b]?.some((t) => t.hasMessages) ||
          emailMessagesCounts[projects[b]?.projectKey] > 0;

      // Tier 1: projects with unread messages first
      if (aHasUnread && !bHasUnread) return -1;
      if (!aHasUnread && bHasUnread) return 1;

      // Within same unread tier: sort by latest message timestamp DESC
      if (aHasUnread && bHasUnread) {
        const aTs = latestTs[a] || '';
        const bTs = latestTs[b] || '';
        if (aTs > bTs) return -1;
        if (aTs < bTs) return 1;
        return sortAlphabetically(projects[a]?.name, projects[b]?.name);
      }

      // Tier 2: projects with any messages before those without
      if (aHasMessages && !bHasMessages) return -1;
      if (!aHasMessages && bHasMessages) return 1;

      // Both have messages (no unread): sort by latest message timestamp DESC
      if (aHasMessages && bHasMessages) {
        const aTs = latestTs[a] || '';
        const bTs = latestTs[b] || '';
        if (aTs > bTs) return -1;
        if (aTs < bTs) return 1;
        return sortAlphabetically(projects[a]?.name, projects[b]?.name);
      }

      // Tier 3: projects without messages (only when search is active) — alphabetical
      return sortAlphabetically(projects[a]?.name, projects[b]?.name);
    });
  }, [
    threadsByProject,
    projects,
    filteredEmployerProjects,
    unreadMessages,
    emailMessagesCounts,
    emailLastMessageAt,
    sortAlphabetically,
  ]);

  // Get email permissions for each project
  const projectEmailPermissions = useSelector((state) => {
    const permissions = {};
    sortedProjectIds.forEach((projectId) => {
      permissions[projectId] = selectIsRequiredPermissionForProject(state, {
        projectId,
        permission: PROJECT_EMAIL_READ,
      });
    });
    return permissions;
  });

  // Find private threads where directRecipient user data is missing and load them
  useEffect(() => {
    const directRecipientIdsToLoad = [];
    Object.values(threads).forEach((thread) => {
      if (thread.directRecipient && !allUsers[thread.directRecipient]) {
        directRecipientIdsToLoad.push(thread.directRecipient);
      }
    });
    if (directRecipientIdsToLoad.length > 0) {
      dispatch(
        loadChatUsers({
          userIds: directRecipientIdsToLoad,
          addToCurrentUsers: false,
        })
      );
    }
  }, [threads, allUsers, dispatch]);

  // Memoized render function for thread items
  const renderThreadItem = React.useCallback(
    (
      thread,
      projectKey,
      projectEmailEnabled,
      hasPermission,
      unreadEmailCount
    ) => {
      const directRecipient = thread.directRecipient
        ? allUsers[thread.directRecipient]
        : null;
      return (
        <ChatListItem
          key={thread.id}
          title={thread.title}
          classes={classes}
          avatarSrc={directRecipient?.image}
          showAvatar={!!thread.directRecipient}
          showEmailButton={
            projectEmailEnabled &&
            hasPermission &&
            !thread.isSubthread &&
            !thread.directRecipient
          }
          showChatButton={true}
          projectKey={projectKey}
          unreadEmailCount={unreadEmailCount}
          unreadChatCount={thread.unreadMessagesCount}
          onEmailButtonClick={onClose}
          onChatButtonClick={() => handleChatButtonClick(thread)}
        />
      );
    },
    [allUsers, classes, handleChatButtonClick, onClose]
  );

  return (
    <div className={classes.root}>
      <Hidden smUp>
        <div className={classes.header}>
          <IconButton className={classes.closeButton} onClick={onClose}>
            <Icon path={mdiClose} size={1} />
          </IconButton>
        </div>
      </Hidden>

      <div className={classes.searchContainer}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder={t('common.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon path={mdiMagnify} size={0.8} />
              </InputAdornment>
            ),
          }}
        />
      </div>
      {Object.keys(threadsByProject).length === 0 &&
      filteredEmployerProjects.length === 0 ? (
        <div className={classes.noMessages}>
          {searchQuery ? t('chat.no_results') : t('chat.no_messages')}
        </div>
      ) : (
        sortedProjectIds.map((projectId) => {
          const project = projects[projectId];
          const projectName = project?.name || projectId;
          const threads = threadsByProject[projectId] || [];
          const projectKey = project?.projectKey || '';
          const unreadEmailCount = Math.max(
            0,
            Number(unreadMessages[projectKey]) || 0
          );
          const projectEmailEnabled = isFeatureEnabled(
            Feature.PROJECT_EMAIL,
            project
          );
          const hasPermission = projectEmailPermissions[projectId];
          return (
            <React.Fragment key={projectId}>
              <Typography className={classes.projectHeader}>
                {projectName}
              </Typography>
              {filteredEmployerProjects.find(
                (empProject) => empProject.id === projectId
              ) && (
                <ChatListItem
                  title={projectName}
                  classes={classes}
                  showEmailButton={true}
                  showChatButton={false}
                  projectKey={projectKey}
                  unreadEmailCount={unreadEmailCount}
                  onEmailButtonClick={onClose}
                />
              )}
              {threads.map((thread) =>
                renderThreadItem(
                  thread,
                  projectKey,
                  projectEmailEnabled,
                  hasPermission,
                  unreadEmailCount
                )
              )}
            </React.Fragment>
          );
        })
      )}
    </div>
  );
};

ChatMenuContent.propTypes = {
  onClose: PropTypes.func,
};

export default ChatMenuContent;
